import {
    TeachingEvaluationCampaign,
    TeachingEvaluationCampaignInput,
    TeachingEvaluationStatus,
} from './types';

const STORAGE_KEY = 'teachingEvaluationCampaigns.v1';
const COMPLETED_KEY = 'teachingEvaluationCompleted.v1';
const HIDDEN_KEY = 'teachingEvaluationHidden.v1';

const makeId = () =>
    `te_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const readCampaigns = (): TeachingEvaluationCampaign[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeCampaigns = (campaigns: TeachingEvaluationCampaign[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(campaigns));
};

const readCompleted = (): string[] => {
    try {
        const raw = localStorage.getItem(COMPLETED_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeCompleted = (keys: string[]) => {
    localStorage.setItem(COMPLETED_KEY, JSON.stringify(keys));
};

const readHidden = (): string[] => {
    try {
        const raw = localStorage.getItem(HIDDEN_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeHidden = (ids: string[]) => {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(ids));
};

const completedKey = (campaignId: string, accountId: string, lmsModuleId?: string) => {
    const base = `${campaignId}:${accountId}`;
    const moduleId = String(lmsModuleId || '').trim();
    return moduleId ? `${base}:${moduleId}` : base;
};

export const resolveStatusFromDates = (
    openAt: string,
    closeAt: string,
    fallback: TeachingEvaluationStatus = 'draft',
): TeachingEvaluationStatus => {
    const now = Date.now();
    const open = new Date(openAt).getTime();
    const close = new Date(closeAt).getTime();
    if (Number.isNaN(open) || Number.isNaN(close)) return fallback;
    if (now < open) return 'scheduled';
    if (now > close) return 'completed';
    return 'active';
};

export const mockStore = {
    list(teamId: string): TeachingEvaluationCampaign[] {
        return readCampaigns()
            .filter((c) => c.teamId === teamId)
            .map((c) => {
                if (c.status === 'draft') return c;
                return {
                    ...c,
                    status: resolveStatusFromDates(c.openAt, c.closeAt, c.status),
                };
            })
            .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },

    get(id: string): TeachingEvaluationCampaign | null {
        return readCampaigns().find((c) => c.id === id) ?? null;
    },

    create(input: TeachingEvaluationCampaignInput): TeachingEvaluationCampaign {
        const now = new Date().toISOString();
        const status =
            input.status ??
            (input.openAt
                ? resolveStatusFromDates(input.openAt, input.closeAt, 'draft')
                : 'draft');
        const campaign: TeachingEvaluationCampaign = {
            ...input,
            id: makeId(),
            status,
            createdAt: now,
            updatedAt: now,
        };
        const all = readCampaigns();
        all.push(campaign);
        writeCampaigns(all);
        return campaign;
    },

    update(
        id: string,
        input: Partial<TeachingEvaluationCampaignInput> & { status?: TeachingEvaluationStatus },
    ): TeachingEvaluationCampaign | null {
        const all = readCampaigns();
        const idx = all.findIndex((c) => c.id === id);
        if (idx < 0) return null;
        const prev = all[idx];
        const next: TeachingEvaluationCampaign = {
            ...prev,
            ...input,
            id: prev.id,
            createdAt: prev.createdAt,
            updatedAt: new Date().toISOString(),
        };
        if (input.status == null && input.openAt && input.closeAt && prev.status !== 'draft') {
            next.status = resolveStatusFromDates(input.openAt, input.closeAt, prev.status);
        }
        all[idx] = next;
        writeCampaigns(all);
        return next;
    },

    remove(id: string): boolean {
        const all = readCampaigns();
        const next = all.filter((c) => c.id !== id);
        if (next.length === all.length) return false;
        writeCampaigns(next);
        return true;
    },

    isHidden(id: string): boolean {
        return Boolean(id) && readHidden().includes(id);
    },

    hideLocal(id: string): boolean {
        if (!id) return false;
        const ids = readHidden();
        if (!ids.includes(id)) {
            ids.push(id);
            writeHidden(ids);
        }
        this.remove(id);
        return true;
    },

    cancel(id: string): TeachingEvaluationCampaign | null {
        return this.update(id, { status: 'draft' });
    },

    getActiveForModule(teamId: string, moduleId: string): TeachingEvaluationCampaign | null {
        return (
            this.list(teamId).find(
                (c) => c.moduleId === moduleId && c.status === 'active',
            ) ?? null
        );
    },

    isCompleted(campaignId: string, accountId: string, lmsModuleId?: string): boolean {
        if (!accountId) return false;
        return readCompleted().includes(completedKey(campaignId, accountId, lmsModuleId));
    },

    markCompleted(campaignId: string, accountId: string, lmsModuleId?: string): void {
        if (!accountId) return;
        const keys = readCompleted();
        const key = completedKey(campaignId, accountId, lmsModuleId);
        if (!keys.includes(key)) {
            keys.push(key);
            writeCompleted(keys);
        }
    },

    upsert(campaign: TeachingEvaluationCampaign): TeachingEvaluationCampaign {
        const all = readCampaigns();
        const idx = all.findIndex((c) => c.id === campaign.id);
        const now = new Date().toISOString();
        if (idx < 0) {
            const created: TeachingEvaluationCampaign = {
                ...campaign,
                createdAt: campaign.createdAt || now,
                updatedAt: now,
            };
            all.push(created);
            writeCampaigns(all);
            return created;
        }
        const next: TeachingEvaluationCampaign = {
            ...all[idx],
            ...campaign,
            id: all[idx].id,
            createdAt: all[idx].createdAt,
            updatedAt: now,
        };
        all[idx] = next;
        writeCampaigns(all);
        return next;
    },
};
import {
    TeachingEvaluationCampaign,
    TeachingEvaluationCampaignInput,
    TeachingEvaluationStatus,
} from './types';

const STORAGE_KEY = 'teachingEvaluationCampaigns.v1';
const COMPLETED_KEY = 'teachingEvaluationCompleted.v1';
const HIDDEN_KEY = 'teachingEvaluationHidden.v1';

const makeId = () =>
    `te_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const readCampaigns = (): TeachingEvaluationCampaign[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeCampaigns = (campaigns: TeachingEvaluationCampaign[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(campaigns));
};

const readCompleted = (): string[] => {
    try {
        const raw = localStorage.getItem(COMPLETED_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeCompleted = (keys: string[]) => {
    localStorage.setItem(COMPLETED_KEY, JSON.stringify(keys));
};

const readHidden = (): string[] => {
    try {
        const raw = localStorage.getItem(HIDDEN_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeHidden = (ids: string[]) => {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(ids));
};

const completedKey = (campaignId: string, accountId: string, lmsModuleId?: string) => {
    const base = `${campaignId}:${accountId}`;
    const moduleId = String(lmsModuleId || '').trim();
    return moduleId ? `${base}:${moduleId}` : base;
};

export const resolveStatusFromDates = (
    openAt: string,
    closeAt: string,
    fallback: TeachingEvaluationStatus = 'draft',
): TeachingEvaluationStatus => {
    const now = Date.now();
    const open = new Date(openAt).getTime();
    const close = new Date(closeAt).getTime();
    if (Number.isNaN(open) || Number.isNaN(close)) return fallback;
    if (now < open) return 'scheduled';
    if (now > close) return 'completed';
    return 'active';
};

export const mockStore = {
    list(teamId: string): TeachingEvaluationCampaign[] {
        return readCampaigns()
            .filter((c) => c.teamId === teamId)
            .map((c) => {
                if (c.status === 'draft') return c;
                return {
                    ...c,
                    status: resolveStatusFromDates(c.openAt, c.closeAt, c.status),
                };
            })
            .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },

    get(id: string): TeachingEvaluationCampaign | null {
        return readCampaigns().find((c) => c.id === id) ?? null;
    },

    create(input: TeachingEvaluationCampaignInput): TeachingEvaluationCampaign {
        const now = new Date().toISOString();
        const status =
            input.status ??
            (input.openAt
                ? resolveStatusFromDates(input.openAt, input.closeAt, 'draft')
                : 'draft');
        const campaign: TeachingEvaluationCampaign = {
            ...input,
            id: makeId(),
            status,
            createdAt: now,
            updatedAt: now,
        };
        const all = readCampaigns();
        all.push(campaign);
        writeCampaigns(all);
        return campaign;
    },

    update(
        id: string,
        input: Partial<TeachingEvaluationCampaignInput> & { status?: TeachingEvaluationStatus },
    ): TeachingEvaluationCampaign | null {
        const all = readCampaigns();
        const idx = all.findIndex((c) => c.id === id);
        if (idx < 0) return null;
        const prev = all[idx];
        const next: TeachingEvaluationCampaign = {
            ...prev,
            ...input,
            id: prev.id,
            createdAt: prev.createdAt,
            updatedAt: new Date().toISOString(),
        };
        if (input.status == null && input.openAt && input.closeAt && prev.status !== 'draft') {
            next.status = resolveStatusFromDates(input.openAt, input.closeAt, prev.status);
        }
        all[idx] = next;
        writeCampaigns(all);
        return next;
    },

    remove(id: string): boolean {
        const all = readCampaigns();
        const next = all.filter((c) => c.id !== id);
        if (next.length === all.length) return false;
        writeCampaigns(next);
        return true;
    },

    isHidden(id: string): boolean {
        return Boolean(id) && readHidden().includes(id);
    },

    hideLocal(id: string): boolean {
        if (!id) return false;
        const ids = readHidden();
        if (!ids.includes(id)) {
            ids.push(id);
            writeHidden(ids);
        }
        this.remove(id);
        return true;
    },

    cancel(id: string): TeachingEvaluationCampaign | null {
        return this.update(id, { status: 'draft' });
    },

    getActiveForModule(teamId: string, moduleId: string): TeachingEvaluationCampaign | null {
        return (
            this.list(teamId).find(
                (c) => c.moduleId === moduleId && c.status === 'active',
            ) ?? null
        );
    },

    isCompleted(campaignId: string, accountId: string, lmsModuleId?: string): boolean {
        if (!accountId) return false;
        return readCompleted().includes(completedKey(campaignId, accountId, lmsModuleId));
    },

    markCompleted(campaignId: string, accountId: string, lmsModuleId?: string): void {
        if (!accountId) return;
        const keys = readCompleted();
        const key = completedKey(campaignId, accountId, lmsModuleId);
        if (!keys.includes(key)) {
            keys.push(key);
            writeCompleted(keys);
        }
    },

    upsert(campaign: TeachingEvaluationCampaign): TeachingEvaluationCampaign {
        const all = readCampaigns();
        const idx = all.findIndex((c) => c.id === campaign.id);
        const now = new Date().toISOString();
        if (idx < 0) {
            const created: TeachingEvaluationCampaign = {
                ...campaign,
                createdAt: campaign.createdAt || now,
                updatedAt: now,
            };
            all.push(created);
            writeCampaigns(all);
            return created;
        }
        const next: TeachingEvaluationCampaign = {
            ...all[idx],
            ...campaign,
            id: all[idx].id,
            createdAt: all[idx].createdAt,
            updatedAt: now,
        };
        all[idx] = next;
        writeCampaigns(all);
        return next;
    },
};
